import {
  fetchYearnApiVaults,
  YEARN_CHAIN_IDS,
  type YDaemonVault,
} from '../../../vaults/yearn/api'
import { getListsMulti } from '../../helpers'
import { YieldFetcher } from '../../types'

/**
 * Intrinsic yield for every endorsed Yearn **V3** vault, on every chain
 * Yearn deploys one — the share-price accrual a lending market must credit
 * when a `yvXXX` token is posted as collateral.
 *
 * Why this exists: the `yearn` VAULT provider has covered these since it
 * shipped, but nothing fed them into the **intrinsic map**, which is a
 * separate wiring (`intrinsicYields[asset.assetGroup]`, read by every
 * lending `convertPublic`/`publicCallParse`). The only Yearn source in
 * that map was a Katana-only fetcher joined through a hand-written
 * 5-address table, so Ethereum's book — `ysyBOLD` (8.6% APR, Morpho Blue
 * collateral at 91.5% LLTV), `yvUSDC-1`, `yvWETH-1`, `yvUSDT-1`,
 * `yvUSDS-1` — read **0% collateral yield**. This replaces that table
 * with the same yDaemon discovery the vault provider already uses.
 *
 * Keys are the token list's `assetGroup` verbatim (the Pendle-fetcher
 * pattern): a vault absent from the list emits nothing, so no phantom
 * keys accumulate as Yearn rotates vaults in and out. That also means the
 * hand-maintained address→group table is gone — and with it its drift
 * (its Katana AUSD entry claimed `vbAUSD yVault::yvvbAUSD` while the list
 * says `AUSD yVault::AUSD`, i.e. that vault's yield landed on a key
 * nothing ever looked up).
 *
 * **APR semantics — `netAPR` only.** That is the realized, net-of-
 * performance-fee accrual to `pricePerShare`, which is exactly what a
 * collateral position earns. `extra.stakingRewardsAPR` is deliberately
 * excluded: it is paid to holders who stake the share token in a gauge,
 * which a token locked in a lending market cannot do. Falls back to
 * `forwardAPR.netAPR` for vaults too new to have a TWRR, mirroring
 * `vaults/yearn/fetchPublic.ts`.
 *
 * Supersedes the former `yearnKatanaFetcher`, whose upstream
 * (`katana-apr-service`) publishes the *same* `netAPR` yDaemon does — but
 * which summed `netAPR + katanaNativeYield`, two names for one number, so
 * every Katana vault's intrinsic yield was reported at **double** its
 * real value.
 */
const toPct = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v * 100 : null

/** Realized net APR, falling back to the forward estimate. */
export const yearnVaultApr = (v: YDaemonVault): number | null =>
  toPct(v.apr?.netAPR) ?? toPct(v.apr?.forwardAPR?.netAPR)

export const yearnFetcher: YieldFetcher = {
  label: 'YEARN',
  fetch: async () => {
    const chainIds = [...YEARN_CHAIN_IDS]
    const lists = await getListsMulti(chainIds)

    const perChain = await Promise.all(
      chainIds.map(async (chainId) => {
        const out: Record<string, number> = {}
        // One chain's yDaemon hiccup must not zero the other six — the
        // fetcher-level `safeFetch` would drop the whole map.
        let vaults: YDaemonVault[] = []
        try {
          vaults = await fetchYearnApiVaults(chainId)
        } catch (e) {
          console.error(`Error fetching YEARN vaults on chain ${chainId}:`, e)
          return out
        }
        for (const v of vaults) {
          const address = (v.address ?? '').toLowerCase()
          const assetKey = lists[chainId]?.list?.[address]?.assetGroup
          if (!assetKey) continue
          const apr = yearnVaultApr(v)
          if (apr == null) continue
          out[assetKey] = apr
        }
        return out
      }),
    )

    return Object.assign({}, ...perChain)
  },
}
